import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  createBase,
  createBaseShift,
  deleteBaseProfileImage,
  getBaseProfileImage$Png,
  uploadBaseProfileImage,
  getBase,
  getBaseManagementAccess,
  getMasterScheduleLegends,
  listBaseCrewUsers,
  ListBaseCrewUsers$Params,
  listBaseLeads,
  listBaseShifts,
  listBaseColors,
  listBases,
  ListBaseLeads$Params,
  ListBases$Params,
  listBaseTimeZones,
  revertBaseCrewLead,
  setBaseCrewLead,
  updateBase,
  updateBaseShifts,
} from '../../../api/scheduler/functions';
import { BaseColorsResponse } from '../../../api/scheduler/models/base-colors-response';
import { BaseProfileImageResponse } from '../../../api/scheduler/models/base-profile-image-response';
import { BaseLeadChangeResponse } from '../../../api/scheduler/models/base-lead-change-response';
import { BaseManagementAccessResponse } from '../../../api/scheduler/models/base-management-access-response';
import { BaseDetailResponse } from '../../../api/scheduler/models/base-detail-response';
import { CreateBaseShiftRequest } from '../../../api/scheduler/models/create-base-shift-request';
import { GetBaseCrewResponse } from '../../../api/scheduler/models/get-base-crew-response';
import { RevertBaseLeadRequest } from '../../../api/scheduler/models/revert-base-lead-request';
import { SetBaseLeadRequest } from '../../../api/scheduler/models/set-base-lead-request';
import { GetBaseLeadsResponse } from '../../../api/scheduler/models/get-base-leads-response';
import { GetBasesResponse } from '../../../api/scheduler/models/get-bases-response';
import { GetBaseTimeZonesResponse } from '../../../api/scheduler/models/get-base-time-zones-response';
import { MasterScheduleLegendsResponse } from '../../../api/scheduler/models/master-schedule-legends-response';
import { SaveBaseRequest } from '../../../api/scheduler/models/save-base-request';
import { UpdateBaseShiftsRequest } from '../../../api/scheduler/models/update-base-shifts-request';
import { SchedulerApiConfiguration } from '../../../api/scheduler/scheduler-api-configuration';
import { BaseShiftOption } from '../models';
import { toBaseShiftOption } from '../utils/base-management.mapper';

@Injectable({ providedIn: 'root' })
export class BaseManagementApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(SchedulerApiConfiguration);

  getAccess(): Observable<BaseManagementAccessResponse> {
    return getBaseManagementAccess(this.http, this.config.rootUrl).pipe(map((res) => res.body));
  }

  list(params?: ListBases$Params): Observable<GetBasesResponse> {
    return listBases(this.http, this.config.rootUrl, params).pipe(map((res) => res.body));
  }

  /** Every Base's shifts, for the Shifts filter. */
  listShifts(): Observable<BaseShiftOption[]> {
    return listBaseShifts(this.http, this.config.rootUrl).pipe(
      map((res) => (res.body.data ?? []).map(toBaseShiftOption))
    );
  }

  get(id: number): Observable<BaseDetailResponse> {
    return getBase(this.http, this.config.rootUrl, { id }).pipe(map((res) => res.body));
  }

  create(body: SaveBaseRequest): Observable<BaseDetailResponse> {
    return createBase(this.http, this.config.rootUrl, { body }).pipe(map((res) => res.body));
  }

  update(id: number, body: SaveBaseRequest): Observable<BaseDetailResponse> {
    return updateBase(this.http, this.config.rootUrl, { id, body }).pipe(map((res) => res.body));
  }

  createShift(id: number, body: CreateBaseShiftRequest): Observable<BaseDetailResponse> {
    return createBaseShift(this.http, this.config.rootUrl, { id, body }).pipe(
      map((res) => res.body)
    );
  }

  updateShifts(id: number, body: UpdateBaseShiftsRequest): Observable<BaseDetailResponse> {
    return updateBaseShifts(this.http, this.config.rootUrl, { id, body }).pipe(
      map((res) => res.body)
    );
  }

  listLeads(params?: ListBaseLeads$Params): Observable<GetBaseLeadsResponse> {
    return listBaseLeads(this.http, this.config.rootUrl, params).pipe(map((res) => res.body));
  }

  listColors(): Observable<BaseColorsResponse> {
    return listBaseColors(this.http, this.config.rootUrl).pipe(map((res) => res.body));
  }

  /** Schedule legends, for the Event, Training, Vacation and Leave colours a Base must avoid. */
  getScheduleLegends(): Observable<MasterScheduleLegendsResponse> {
    return getMasterScheduleLegends(this.http, this.config.rootUrl).pipe(map((res) => res.body));
  }

  listTimeZones(): Observable<GetBaseTimeZonesResponse> {
    return listBaseTimeZones(this.http, this.config.rootUrl).pipe(map((res) => res.body));
  }

  listBaseCrewUsers(params: ListBaseCrewUsers$Params): Observable<GetBaseCrewResponse> {
    return listBaseCrewUsers(this.http, this.config.rootUrl, params).pipe(map((res) => res.body));
  }

  setBaseCrewLead(id: number, body: SetBaseLeadRequest): Observable<BaseLeadChangeResponse> {
    return setBaseCrewLead(this.http, this.config.rootUrl, { id, body }).pipe(map((res) => res.body));
  }

  revertBaseCrewLead(id: number, body: RevertBaseLeadRequest): Observable<BaseDetailResponse> {
    return revertBaseCrewLead(this.http, this.config.rootUrl, { id, body }).pipe(map((res) => res.body));
  }

  uploadProfileImage(id: number, file: Blob): Observable<BaseProfileImageResponse> {
    return uploadBaseProfileImage(this.http, this.config.rootUrl, { id, body: { file } }).pipe(
      map((res) => res.body)
    );
  }

  deleteProfileImage(id: number): Observable<BaseProfileImageResponse> {
    return deleteBaseProfileImage(this.http, this.config.rootUrl, { id }).pipe(
      map((res) => res.body)
    );
  }

  /** The image bytes (authenticated, so it cannot be an `<img src>`); errors with 404 when none. */
  getProfileImage(id: number): Observable<Blob> {
    return getBaseProfileImage$Png(this.http, this.config.rootUrl, { id }).pipe(
      map((res) => res.body)
    );
  }
}
